import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { injectIntl } from 'react-intl';
import CSSModules from 'react-css-modules';

import { PASSWORD_RULES, PASSWORD_STATUS } from '../../constants/passwordRules';
import { useToggleFlag } from '../../hocs/useToogleFlag';
import ClickableElement from '../ClickableElement';
import CheckingListTooltip from '../CheckPasswordList';
import { COLORS } from '../../constants/styles';
import { AUTH } from '../../constants/strings';
import { FIELDS_TYPE } from '../../constants';
import Icon from '../Icon';
import TextInput from '.';
import styles from './index.styl';

const DELAY = 300;

function PasswordInput({
  name,
  label,
  onBlur,
  onChange,
  className,
  defaultType,
  withValidation,
  intl: { formatMessage },
  placeholder = formatMessage(AUTH.fields.password),
  shouldClearWhenOnFocus,
  ...other
}) {
  const { DEFAULT, DANGER, DONE } = PASSWORD_STATUS;
  const initialList = PASSWORD_RULES.map(item => {
    return { ...item, status: DEFAULT };
  });

  const initialState = {
    invalid: false,
    list: initialList,
    type: defaultType || FIELDS_TYPE.PASSWORD,
  };

  const [inputState, setInputState] = useState(initialState);
  const [openedChecker, openChecker, closeChecker] = useToggleFlag();
  const { invalid, list, type } = inputState;

  const isPasswordInput = useMemo(() => {
    return type === FIELDS_TYPE.PASSWORD;
  }, [type]);

  const validate = value => {
    const validatedList = list.map(item => {
      const danger = !value.match(item.rule);
      return { ...item, status: danger ? DANGER : DONE };
    });
    const isInvalid = validatedList.some(item => item.status === DANGER);
    return { isInvalid, validatedList };
  };

  const handleChange = value => {
    const { isInvalid, validatedList = initialList } =
      withValidation && value ? validate(value) : {};
    setInputState({ ...inputState, list: validatedList, invalid: isInvalid });
    if (onChange) onChange(name, value, isInvalid);
  };

  const handleFocus = () => {
    if (shouldClearWhenOnFocus) handleChange('');
    if (withValidation) {
      openChecker();
    }
  };

  const handleBlur = value => {
    if (withValidation) {
      closeChecker();
    }
    if (onBlur) {
      onBlur(value);
    }
  };

  const togglePasswordVisibility = () => {
    const inputType = isPasswordInput ? FIELDS_TYPE.TEXT : FIELDS_TYPE.PASSWORD;
    setInputState({ ...inputState, type: inputType });
  };

  return (
    <div className={className} styleName="passwordInput">
      <TextInput
        name={name}
        type={type}
        invalid={invalid}
        placeholder={placeholder}
        label={label || placeholder}
        onChange={throttle(handleChange, DELAY)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        shouldClearWhenOnFocus={shouldClearWhenOnFocus}
        {...other}
      >
        <div styleName="toggler">
          <ClickableElement
            onClick={togglePasswordVisibility}
            className={classNames({ dn: isPasswordInput })}
          >
            <Icon
              name="password-invisible"
              style={{ fill: COLORS.bluePrimary }}
            />
          </ClickableElement>
          <ClickableElement
            onClick={togglePasswordVisibility}
            className={classNames({ dn: !isPasswordInput })}
            ariaLabel="yey"
          >
            <Icon
              name="password-visible"
              style={{ fill: COLORS.bluePrimary }}
            />
          </ClickableElement>
        </div>
      </TextInput>
      <If condition={openedChecker}>
        <CheckingListTooltip list={list} />
      </If>
    </div>
  );
}

PasswordInput.defaultProps = {
  name: FIELDS_TYPE.PASSWORD,
};

PasswordInput.propTypes = {
  className: PropTypes.string,
  withValidation: PropTypes.bool,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  defaultType: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  shouldClearWhenOnFocus: PropTypes.bool,
};

export default injectIntl(
  CSSModules(PasswordInput, styles, { allowMultiple: true }),
);



// WEBPACK FOOTER //
// ./src/components/TextInput/PasswordInput.jsx